import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';

const quickLinks = [
  { name: 'Home', href: '#home' },
  { name: 'Collections', href: '#collections' },
  { name: 'Wholesale', href: '#wholesale' },
  { name: 'About Us', href: '#about' },
  { name: 'Sustainability', to: '/sustainability' },
];

const productLinks = [
  { name: 'Jute Bags', href: '#collections' },
  { name: 'Cotton Canvas Bags', href: '#collections' },
  { name: 'Pouches', href: '#collections' },
  { name: 'Wine Bags', href: '#collections' },
];

const socialLinks = [
  { name: 'Facebook', icon: Facebook, href: '#' },
  { name: 'Instagram', icon: Instagram, href: '#' },
  { name: 'LinkedIn', icon: Linkedin, href: '#' },
  { name: 'Twitter', icon: Twitter, href: '#' },
];

export const Footer = () => {
  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      id="contact"
      className="bg-[#1e3428] text-white py-12 md:py-16 border-t border-[#182d22]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 space-y-8 md:space-y-0">
          {/* Brand Column */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="flex items-center gap-2 mb-4">
              <img
                src={`${process.env.PUBLIC_URL || ''}/footerlogo.png`}
                alt="Bags4Ever - Sustainable by Nature"
                className="h-24 w-auto"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5 max-w-md md:max-w-none">
              Bags4Ever specializes in premium sustainable bags crafted for global retail and wholesale
              markets. We combine eco-friendly materials, modern design, and durable manufacturing
              standards to deliver high-quality products that meet international export requirements.
              Our commitment to sustainability and craftsmanship ensures long-lasting solutions for
              brands worldwide.
            </p>
            {/* Social Links - centered on mobile */}
            <div className="flex gap-2 justify-center md:justify-start">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#2a4a3a] flex items-center justify-center transition-colors duration-200"
                  aria-label={social.name}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold mb-4 !text-white">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.to ? (
                    <Link
                      to={link.to}
                      className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                      data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                      data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-base font-semibold mb-4 !text-white">
              Products
            </h3>
            <ul className="space-y-2">
              {productLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base font-semibold mb-2 !text-white">
              Contact Us
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-white/70 flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">
                  <span className="font-semibold text-white">Bags4Ever Inc.</span>
                  <br />
                  Nevada, USA
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-white/70 flex-shrink-0" />
                <a
                  href="mailto:info@bags4ever.com"
                  className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                  data-testid="footer-email"
                >
                  info@bags4ever.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-white/70 flex-shrink-0" />
                <a
                  href="tel:+12134712170"
                  className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                  data-testid="footer-phone"
                >
                  +1 213-471-2170
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-white/70 flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">
                  <span className="font-semibold text-white">India – Registered Office</span>
                  <br />
                  5, Gujrawala Town - II,<br />
                  Delhi - 110009, India
                </span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-white/70 flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">
                  <span className="font-semibold text-white">Manufacturing &amp; Sourcing Support</span>
                  <br />
                  Reliable Exports – India
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-white/70 flex-shrink-0" />
                <a
                  href="tel:+919810272098"
                  className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                >
                  +91 98102 72098
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-white/70 flex-shrink-0" />
                <a
                  href="mailto:sales@reliableexports.in"
                  className="text-white/60 hover:text-white transition-colors duration-200 text-sm"
                >
                  sales@reliableexports.in
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <div className="text-center md:text-left">
              <p className="text-white/40 text-sm">
                © {new Date().getFullYear()} Bags4Ever. All rights reserved.
              </p>
              <p className="text-white/40 text-sm mt-1">
                This website is owned and operated by Bags4Ever Inc.
              </p>
            </div>
            <div className="flex gap-4">
              <a
                href="#"
                className="text-white/40 hover:text-white transition-colors duration-200 text-sm"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-white/40 hover:text-white transition-colors duration-200 text-sm"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
